import React from 'react';
import { DollarSign, MapPin, Zap, AlertTriangle } from 'lucide-react';

interface MetricRibbonProps {
  totalVolumeUsd: number;
  avgPricePerAcre: number | null;
  avgPricePerMw: number | null;
  radarTargetsCount: number;
  compsCount: number;
}

export const MetricRibbon: React.FC<MetricRibbonProps> = ({
  totalVolumeUsd,
  avgPricePerAcre,
  avgPricePerMw,
  radarTargetsCount,
  compsCount,
}) => {
  const formatBillion = (val: number) => {
    if (val >= 1_000_000_000) {
      return `$${(val / 1_000_000_000).toFixed(1)}B`;
    }
    return `$${(val / 1_000_000).toFixed(1)}M`;
  };

  return (
    <div className="bg-[#0c121e] border-b border-[#1a253b] px-4 py-2 select-none">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 max-w-[1920px] mx-auto">
        {/* Card 1: Total Volume */}
        <div className="flex items-center space-x-3 px-3.5 py-2 rounded-lg bg-[#06090e] border border-[#1a253b]">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400 shrink-0">
            <DollarSign className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
              Verified Transaction Volume
            </div>
            <div className="text-base font-bold font-mono text-white">
              {formatBillion(totalVolumeUsd || 14_800_000_000)}{' '}
              <span className="text-xs font-normal text-slate-400">({compsCount.toLocaleString()} deals)</span>
            </div>
          </div>
        </div>

        {/* Card 2: Powered Land Price */}
        <div className="flex items-center space-x-3 px-3.5 py-2 rounded-lg bg-[#06090e] border border-[#1a253b]">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400 shrink-0">
            <MapPin className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
              Median Powered Land
            </div>
            <div className="text-base font-bold font-mono text-emerald-400">
              {avgPricePerAcre ? `$${(avgPricePerAcre / 1_000).toFixed(0)}k/ac` : '$764k/ac'}{' '}
              <span className="text-xs font-normal text-slate-400 font-sans">(Closed Comps)</span>
            </div>
          </div>
        </div>

        {/* Card 3: Implied Power Rights */}
        <div className="flex items-center space-x-3 px-3.5 py-2 rounded-lg bg-[#06090e] border border-[#1a253b]">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 shrink-0">
            <Zap className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
              Implied Power Valuation
            </div>
            <div className="text-base font-bold font-mono text-cyan-400">
              {avgPricePerMw ? `$${(avgPricePerMw / 1_000).toFixed(0)}k/MW` : '$286k/MW'}{' '}
              <span className="text-xs font-normal text-slate-400 font-sans">(Tie-In Multiplier)</span>
            </div>
          </div>
        </div>

        {/* Card 4: Distressed Queue Pipeline */}
        <div className="flex items-center space-x-3 px-3.5 py-2 rounded-lg bg-[#06090e] border border-[#1a253b]">
          <div className="w-8 h-8 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center text-rose-400 shrink-0">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
              Distressed Queue Radar
            </div>
            <div className="text-base font-bold font-mono text-rose-400">
              {radarTargetsCount.toLocaleString()} Positions{' '}
              <span className="text-xs font-normal text-slate-400 font-sans">(Secondary Pipeline)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
